import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import {
  buildFilterOptionsFromRows,
  fetchPortfolioProjects,
  type HealthFilter,
  type PortfolioFilterOptions,
  type PortfolioProjectRow,
  type PortfolioQueryParams,
  type PortfolioSortField,
  type PortfolioStats,
  type PortfolioStatusPreset,
  type SortDirection,
  type SowStatus,
} from '@/api/portfolio/api';
import type { HealthFilterValue } from '@/components/common/HealthIndicator';
import {
  applyStatusPreset as applyStatusPresetToRows,
  computePortfolioStats,
  filterPortfolioRows,
  paginatePortfolioRows,
  sortPortfolioRows,
  stripFinancialFields,
} from '@/components/pages/portfolio/portfolio_table.utils';

/** The four Health filter fields (More filters → Health), each an RYG value or `all`. */
export interface PortfolioHealthFilters {
  overall_status: HealthFilterValue;
  on_schedule: HealthFilterValue;
  csat: HealthFilterValue;
  team_health: HealthFilterValue;
}

/** The user-editable filter state (everything except the stat-card preset). */
export interface PortfolioFilters extends PortfolioHealthFilters {
  client_id: string[];
  project_id: string[];
  project_name: string;
  offering: string[];
  leader: string[];
  sow_status: SowStatus | 'all';
  starts_on_before: string | null;
  ends_on_after: string | null;
}

interface PortfolioControlState {
  filters: PortfolioFilters;
  status: PortfolioStatusPreset | null;
  page: number;
  perPage: number;
  sortBy: PortfolioSortField | null;
  sortDir: SortDirection;
}

const DEFAULT_PER_PAGE = 10;

const initialFilters: PortfolioFilters = {
  client_id: [],
  project_id: [],
  project_name: '',
  offering: [],
  leader: [],
  sow_status: 'active',
  overall_status: 'all',
  on_schedule: 'all',
  csat: 'all',
  team_health: 'all',
  starts_on_before: null,
  ends_on_after: null,
};

const initialControlState: PortfolioControlState = {
  filters: initialFilters,
  status: null,
  page: 1,
  perPage: DEFAULT_PER_PAGE,
  sortBy: null,
  sortDir: 'asc',
};

type PortfolioControlAction =
  | { type: 'SET_FILTER'; payload: Partial<PortfolioFilters> }
  | { type: 'SET_STATUS_PRESET'; payload: PortfolioStatusPreset | null }
  | { type: 'CLEAR_ALL' }
  | { type: 'SET_SORT'; payload: { sortBy: PortfolioSortField | null; sortDir: SortDirection } }
  | { type: 'SET_PAGE'; payload: { page: number; perPage: number } };

const controlReducer = (
  state: PortfolioControlState,
  action: PortfolioControlAction,
): PortfolioControlState => {
  switch (action.type) {
    case 'SET_FILTER':
      // Any filter change resets to page 1 (mirrors the client-directory pattern).
      return { ...state, filters: { ...state.filters, ...action.payload }, page: 1 };
    case 'SET_STATUS_PRESET':
      return { ...state, status: state.status === action.payload ? null : action.payload, page: 1 };
    case 'CLEAR_ALL':
      return { ...initialControlState, filters: { ...initialFilters, sow_status: 'all' } };
    case 'SET_SORT':
      return { ...state, sortBy: action.payload.sortBy, sortDir: action.payload.sortDir, page: 1 };
    case 'SET_PAGE':
      return { ...state, page: action.payload.page, perPage: action.payload.perPage };
    default:
      return state;
  }
};

/** `all` maps to `undefined` so the filter is omitted entirely. */
const healthParam = (value: HealthFilterValue): HealthFilter | undefined =>
  value === 'all' ? undefined : value;

const buildQuery = (state: PortfolioControlState): PortfolioQueryParams => ({
  client_id: state.filters.client_id.length ? state.filters.client_id : undefined,
  project_id: state.filters.project_id.length ? state.filters.project_id : undefined,
  project_name: state.filters.project_name.trim() || undefined,
  offering: state.filters.offering.length ? state.filters.offering : undefined,
  leader: state.filters.leader.length ? state.filters.leader : undefined,
  sow_status: state.filters.sow_status,
  overall_status: healthParam(state.filters.overall_status),
  on_schedule: healthParam(state.filters.on_schedule),
  csat: healthParam(state.filters.csat),
  team_health: healthParam(state.filters.team_health),
  starts_on_before: state.filters.starts_on_before,
  ends_on_after: state.filters.ends_on_after,
  status: state.status,
  page: state.page,
  per_page: state.perPage,
  sort_by: state.sortBy ?? undefined,
  sort_dir: state.sortDir,
});

export interface UsePortfolioOptions {
  accessToken?: string | null;
  isDirectorOrHigher: boolean;
}

export interface UsePortfolioResult {
  rows: PortfolioProjectRow[];
  /** The whole, unfiltered dataset loaded from the server — used by CSV export. */
  allRows: PortfolioProjectRow[];
  total: number;
  page: number;
  perPage: number;
  stats: PortfolioStats;
  filterOptions: PortfolioFilterOptions;
  loading: boolean;
  error: string | null;
  filters: PortfolioFilters;
  status: PortfolioStatusPreset | null;
  sortBy: PortfolioSortField | null;
  sortDir: SortDirection;
  /** Current query params (used by the export button). */
  query: PortfolioQueryParams;
  setClientFilter: (value: string[]) => void;
  setProjectFilter: (value: string[]) => void;
  setProjectSearch: (value: string) => void;
  setOfferingFilter: (value: string[]) => void;
  setLeaderFilter: (value: string[]) => void;
  setSowStatus: (value: SowStatus | 'all') => void;
  setHealthFilter: (value: Partial<PortfolioHealthFilters>) => void;
  setDateRange: (value: {
    starts_on_before?: string | null;
    ends_on_after?: string | null;
  }) => void;
  applyStatusPreset: (preset: PortfolioStatusPreset | null) => void;
  clearAllFilters: () => void;
  setSort: (sortBy: PortfolioSortField | null, sortDir: SortDirection) => void;
  changePage: (page: number, perPage: number) => void;
  retry: () => void;
}

/**
 * Orchestrates the portfolio view: fetches the whole portfolio once, then
 * derives filtering, sorting, pagination, stats, and dropdown options
 * client-side (see `portfolio_table.utils.ts`) — no network call on filter,
 * sort, page, or stat-card preset changes.
 */
export const usePortfolio = ({
  accessToken,
  isDirectorOrHigher,
}: UsePortfolioOptions): UsePortfolioResult => {
  const [state, dispatch] = useReducer(controlReducer, initialControlState);
  const [allRows, setAllRows] = useState<PortfolioProjectRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState<number>(0);

  const query = useMemo(() => buildQuery(state), [state]);
  const requestIdRef = useRef(0);
  const inflightRef = useRef<{
    key: string;
    promise: Promise<PortfolioProjectRow[]>;
  } | null>(null);

  useEffect(() => {
    if (!accessToken) {
      return undefined;
    }
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    let cancelled = false;
    setLoading(true);
    setError(null);

    // Canonical key encodes the full request identity: method, URL, token, params,
    // body, and explicit reload signal. Adding query params or a body here in the
    // future will automatically produce a distinct key and bypass deduplication.
    const requestKey = JSON.stringify({
      method: 'GET',
      url: '/portfolio/projects',
      token: accessToken,
      params: null,
      body: null,
      reload: reloadToken,
    });

    const attachHandlers = (promise: Promise<PortfolioProjectRow[]>) => {
      void promise
        .then((rows) => {
          if (cancelled || requestId !== requestIdRef.current) {
            return;
          }
          setAllRows(rows);
        })
        .catch((error_: unknown) => {
          if (cancelled || requestId !== requestIdRef.current) {
            return;
          }
          setError(error_ instanceof Error ? error_.message : 'Request failed');
        })
        .finally(() => {
          if (!cancelled && requestId === requestIdRef.current) {
            setLoading(false);
          }
        });
    };

    const inflight = inflightRef.current;
    if (inflight?.key === requestKey) {
      // A fetch with this exact identity is already in flight — attach to it
      // instead of issuing a duplicate request (idempotent data-fetch rule, FE CONSTITUTION).
      attachHandlers(inflight.promise);
    } else {
      const promise = fetchPortfolioProjects(accessToken);
      inflightRef.current = { key: requestKey, promise };
      void promise.finally(() => {
        if (inflightRef.current?.promise === promise) {
          inflightRef.current = null;
        }
      });
      attachHandlers(promise);
    }

    return () => {
      cancelled = true;
    };
  }, [accessToken, reloadToken]);

  const scopedRows = useMemo(
    () => (isDirectorOrHigher ? allRows : allRows.map(stripFinancialFields)),
    [allRows, isDirectorOrHigher],
  );

  // Cascading filter chain (issue #203): SOW Status scopes Client, which
  // scopes Project. `sowScopedRows` narrows to the selected SOW status once,
  // so both the options memo and the client/SOW prune callbacks below stay
  // in sync with a single source of truth for that scope.
  const sowScopedRows = useMemo(
    () =>
      state.filters.sow_status === 'all'
        ? scopedRows
        : scopedRows.filter((row) => row.sow_status === state.filters.sow_status),
    [scopedRows, state.filters.sow_status],
  );

  const filterOptions = useMemo(
    () => buildFilterOptionsFromRows(sowScopedRows, state.filters.client_id),
    [sowScopedRows, state.filters.client_id],
  );
  const filtered = useMemo(() => filterPortfolioRows(scopedRows, query), [scopedRows, query]);
  const stats = useMemo(() => computePortfolioStats(filtered), [filtered]);
  const presetted = useMemo(
    () => applyStatusPresetToRows(filtered, state.status),
    [filtered, state.status],
  );
  const sorted = useMemo(
    () => (state.sortBy ? sortPortfolioRows(presetted, state.sortBy, state.sortDir) : presetted),
    [presetted, state.sortBy, state.sortDir],
  );
  const rows = useMemo(
    () => paginatePortfolioRows(sorted, state.page, state.perPage),
    [sorted, state.page, state.perPage],
  );

  const setClientFilter = useCallback(
    (value: string[]) => {
      // Cascading filter (issue #203): scoping to a client (within the
      // current SOW-status scope) prunes any selected projects that don't
      // belong to it; clearing the client resets the project selection
      // entirely, mirroring the options reset.
      const validProjectNames = new Set(
        sowScopedRows
          .filter((row) => value.includes(row.client_name))
          .map((row) => row.project_name),
      );
      const project_id =
        value.length === 0
          ? []
          : state.filters.project_id.filter((name) => validProjectNames.has(name));
      dispatch({ type: 'SET_FILTER', payload: { client_id: value, project_id } });
    },
    [sowScopedRows, state.filters.project_id],
  );
  const setProjectFilter = useCallback((value: string[]) => {
    dispatch({ type: 'SET_FILTER', payload: { project_id: value } });
  }, []);
  const setProjectSearch = useCallback((value: string) => {
    dispatch({ type: 'SET_FILTER', payload: { project_name: value } });
  }, []);
  const setOfferingFilter = useCallback((value: string[]) => {
    dispatch({ type: 'SET_FILTER', payload: { offering: value } });
  }, []);
  const setLeaderFilter = useCallback((value: string[]) => {
    dispatch({ type: 'SET_FILTER', payload: { leader: value } });
  }, []);
  const setSowStatus = useCallback(
    (value: SowStatus | 'all') => {
      // Cascading filter (issue #203): SOW Status is the top of the chain —
      // scoping to a status prunes any selected client/project that has no
      // row with that status. Clearing back to 'all' only widens the scope,
      // so existing selections stay valid and need no pruning.
      if (value === 'all') {
        dispatch({ type: 'SET_FILTER', payload: { sow_status: value } });
        return;
      }
      const matchingRows = scopedRows.filter((row) => row.sow_status === value);
      const validClientNames = new Set(matchingRows.map((row) => row.client_name));
      const client_id = state.filters.client_id.filter((name) => validClientNames.has(name));
      const validProjectNames = new Set(
        matchingRows
          .filter((row) => client_id.length === 0 || client_id.includes(row.client_name))
          .map((row) => row.project_name),
      );
      const project_id = state.filters.project_id.filter((name) => validProjectNames.has(name));
      dispatch({ type: 'SET_FILTER', payload: { sow_status: value, client_id, project_id } });
    },
    [scopedRows, state.filters.client_id, state.filters.project_id],
  );
  const setHealthFilter = useCallback((value: Partial<PortfolioHealthFilters>) => {
    dispatch({ type: 'SET_FILTER', payload: value });
  }, []);
  const setDateRange = useCallback(
    (value: { starts_on_before?: string | null; ends_on_after?: string | null }) => {
      dispatch({ type: 'SET_FILTER', payload: value });
    },
    [],
  );
  const applyStatusPreset = useCallback((preset: PortfolioStatusPreset | null) => {
    dispatch({ type: 'SET_STATUS_PRESET', payload: preset });
  }, []);
  const clearAllFilters = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL' });
  }, []);
  const setSort = useCallback((sortBy: PortfolioSortField | null, sortDir: SortDirection) => {
    dispatch({ type: 'SET_SORT', payload: { sortBy, sortDir } });
  }, []);
  const changePage = useCallback((page: number, perPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: { page, perPage } });
  }, []);
  const retry = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  return {
    rows,
    allRows: scopedRows,
    total: sorted.length,
    page: state.page,
    perPage: state.perPage,
    stats,
    filterOptions,
    loading,
    error,
    filters: state.filters,
    status: state.status,
    sortBy: state.sortBy,
    sortDir: state.sortDir,
    query,
    setClientFilter,
    setProjectFilter,
    setProjectSearch,
    setOfferingFilter,
    setLeaderFilter,
    setSowStatus,
    setHealthFilter,
    setDateRange,
    applyStatusPreset,
    clearAllFilters,
    setSort,
    changePage,
    retry,
  };
};
